/**
 * Intelligente Related Articles Engine basierend auf Tags
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BlogPost } from '@/types/blog';
import { blogPosts } from '@/data/blog';
import { getBlogPostUrl } from '@/utils/blogUrls';
import { useBlogAnalytics } from '@/hooks/useBlogAnalytics';
import { getTagsByNames } from '@/data/blog/tags/tagSystem';
interface RelatedArticlesProps {
  currentPost: BlogPost;
  maxResults?: number;
  showTags?: boolean;
}
const RelatedArticles: React.FC<RelatedArticlesProps> = ({
  currentPost,
  maxResults = 3,
  showTags = true
}) => {
  // Early return if currentPost is undefined
  if (!currentPost) {
    return null;
  }
  const {
    trackRelatedArticleClick
  } = useBlogAnalytics();

  // Intelligente Artikel-Ähnlichkeit basierend auf Tags
  const getRelatedArticles = (): BlogPost[] => {
    const currentTags = currentPost.tags || [];
    if (currentTags.length === 0) {
    // Fallback: Artikel aus der gleichen Kategorie
    return blogPosts.filter(post => post.slug !== currentPost.slug && post.category === currentPost.category).slice(0, maxResults);
    }

    // Score-basierte Ähnlichkeit
    const scoredPosts = blogPosts.filter(post => post.slug !== currentPost.slug).map(post => {
      const postTags = post.tags || [];
      let score = 0;

      // Direkte Tag-Übereinstimmungen (höchste Priorität)
      const commonTags = currentTags.filter(tag => postTags.includes(tag));
      score += commonTags.length * 3;

      // Kategorie-Übereinstimmung
      if (post.category === currentPost.category) {
        score += 2;
      }

      // Author-Übereinstimmung
      if (post.author === currentPost.author) {
        score += 1;
      }

      // Hierarchische Tag-Verwandtschaft
      const currentTagObjs = getTagsByNames(currentTags);
      const postTagObjs = getTagsByNames(postTags);
      currentTagObjs.forEach(currentTag => {
        postTagObjs.forEach(postTag => {
          // Parent-Child Beziehung
          if (currentTag.parent === postTag.id || postTag.parent === currentTag.id) {
            score += 2;
          }
          // Gleicher Parent (Geschwister-Tags)
          if (currentTag.parent && currentTag.parent === postTag.parent) {
            score += 1;
          }
        });
      });
      return {
        post,
        score
      };
    }).filter(({
      score
    }) => score > 0).sort((a, b) => b.score - a.score).slice(0, maxResults).map(({
      post
    }) => post);
    return scoredPosts;
  };
  const relatedArticles = getRelatedArticles();
  if (relatedArticles.length === 0) {
    return null;
  }
  const handleArticleClick = (targetSlug: string) => {
    trackRelatedArticleClick(targetSlug, currentPost.slug);
  };
  return;
};
export default RelatedArticles;